<section
  class="mx-auto flex w-full max-w-360 flex-1 flex-col p-6 2xl:px-0"
  aria-labelledby="unm-page-heading"
>
  <!-- Page Header -->
  <div
    class="flex w-full flex-wrap items-start justify-between gap-4 pb-4 md:flex-nowrap lg:gap-12"
  >
    <div class="min-w-0">
      <a
        routerLink="/analytics/dashboard"
        class="mb-1 inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-widest text-black"
        aria-label="Back to IQ Analytics"
      >
        IQ Analytics
      </a>
      <h1
        id="unm-page-heading"
        class="m-0 flex items-center gap-2 font-montserrat text-3xl font-bold leading-tight text-gray-900 [text-wrap:auto]"
      >
        Unusual Network Metrics
      </h1>
      <p class="mt-1 text-sm text-gray-400">
        Device behavior, network measurements, and severity insights for the selected time frame.
      </p>
    </div>
    <div
      class="flex flex-wrap items-center justify-center gap-2"
      role="toolbar"
      aria-label="UNM controls"
    >
      @if (canShowSampleDataToggle()) {
        <app-toggle
          [disabled]="isLoading"
          [(ngModel)]="showMockData"
          (ngModelChange)="toggleMockData($event)"
          label="Sample Data"
          size="md"
          color="primary"
        />
      }
      <app-analytics-time-selector
        [disabled]="isLoading"
        (selectedOptionChange)="onChildSelectionChange($event)"
      />
    </div>
  </div>

  <!-- Charts surface -->
  <div class="mt-2">
    <div class="w-full">
      @if (!isNoContent) {
        <div
          class="grid w-full min-w-0 grid-cols-1 gap-[1px] overflow-hidden bg-gray-150 shadow-sm sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5"
          aria-label="Unusual Network Metrics charts"
        >
          <!-- Pie Chart Widget -->
          <div class="flex min-w-0 flex-col bg-white p-4 sm:col-span-1 lg:col-span-1 lg:p-5 xl:col-span-1">
            <h2
              class="m-0 mb-0 flex items-center gap-1.5 font-montserrat text-base font-semibold uppercase tracking-wider text-gray-900"
            >
              <span class="material-icons-outlined text-[16px]" aria-hidden="true"
                >pie_chart</span
              >
              Actionable Insights
            </h2>
            <app-analytics-widget [isNoContent]="isNoContent" [loading]="isLoading" class="h-full w-full">
              @if (hasBarAndTableContent && !isLoading) {
                <app-analytics-apex-chart-pie
                  [series]="severityBreakdownData"
                  [labels]="severityBreakdownLabels"
                  [title]="''"
                  strokeColor="#ffffff"
                  [manualColors]="['#8781FF', '#FFEB40', '#61F29D']"
                  (categoryChange)="calculateCategoryMetrics($event)"
                />
              }
            </app-analytics-widget>
          </div>

          <!-- Measurements Widget -->
          <div class="flex min-w-0 flex-col bg-white p-4 sm:col-span-1 lg:col-span-1 lg:p-5 xl:col-span-1">
            <h2
              class="m-0 mb-0 flex items-center gap-1.5 font-montserrat text-base font-semibold uppercase tracking-wider text-gray-900"
            >
              <span class="material-icons-outlined text-[16px]" aria-hidden="true"
                >speed</span
              >
              Network Measures
            </h2>
            <app-analytics-widget [isNoContent]="isNoContent" [loading]="isLoading" class="h-full w-full">
              @if (hasBarAndTableContent && !isLoading) {
                <app-highlighted-network-measurements
                  [category]="measurementCategory"
                  [dataUtilization]="dataUtilization"
                  [latency]="latency"
                  [jitter]="jitter"
                  [packetLost]="packetLost"
                  class="h-full w-full"
                />
              }
            </app-analytics-widget>
          </div>

          <!-- Bar Chart Widget -->
          <div class="flex min-w-0 flex-col bg-white p-4 sm:col-span-2 lg:col-span-2 lg:p-5 xl:col-span-3">
            <h2
              class="m-0 mb-0 flex items-center gap-1.5 font-montserrat text-base font-semibold uppercase tracking-wider text-gray-900"
            >
              <span class="material-icons-outlined text-[16px]" aria-hidden="true"
                >bar_chart</span
              >
              Top Unusual Devices
            </h2>
            <app-analytics-widget [isNoContent]="isNoContent" [loading]="isLoading" class="h-full w-full">
              @if (!isLoading) {
                <app-analytics-apex-chart-bar
                  [chartName]="''"
                  [data]="topUnusualDevicesData"
                  [categories]="topUnusualDevicesCategories"
                  [yTitle]="topUnusualDevicesYtitle"
                  [height]="320"
                />
              }
            </app-analytics-widget>
          </div>
        </div>
      }
    </div>
  </div>

  <div class="mt-2 w-full">
    @if ((unmRawDeviceEvents$ | async)?.devices?.length && !isLoading) {
      <app-unm-data-table
        [deviceEvents]="(unmRawDeviceEvents$ | async)?.devices"
        [isNoContent]="isNoContent"
        [isLoading]="isLoading"
      />
    } @else {
      <div
        class="flex flex-col items-center justify-center rounded-md border border-gray-150 bg-white p-12 shadow-sm"
        role="status"
        aria-live="polite"
      >
        @if (isLoading) {
          <mat-spinner [diameter]="48" aria-label="Loading unusual network metrics" />
        } @else {
          <mat-icon
            fontSet="material-icons-outlined"
            class="mb-2 !text-5xl text-gray-300"
            aria-hidden="true"
            >network_check</mat-icon
          >
          <h2 class="font-montserrat text-lg font-semibold text-gray-700">No Data Available</h2>
          <p class="mt-1 max-w-sm text-center font-lato text-sm text-gray-500">
            Insufficient device information to generate observation summaries for the selected time frame.
          </p>
        }
      </div>
    }
  </div>
</section>
